import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/section-heading'
import { TreeRings } from '@/components/generals/tree-rings'
import { buttonVariants } from '@/components/ui/button'

interface CtaBannerProps {
    title?: ReactNode
    description?: ReactNode
    primaryHref?: string
    primaryLabel?: string
    secondaryHref?: string
    secondaryLabel?: string
}

export const CtaBanner: FC<CtaBannerProps> = ({
    title = "Ready to plan your tiny house?",
    description = "Tell us how you want to live and where. We will come back with ideas, a realistic price range and the next steps, with no obligation.",
    primaryHref = '/contact',
    primaryLabel = "Request a quote",
    secondaryHref = '/models',
    secondaryLabel = "Explore our models"
}): ReactNode => {
    return (
        <section id='get-started' aria-labelledby='get-started-title' className='px-5 py-16 sm:px-8 lg:py-24'>
            <div className='relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-primary px-6 py-14 text-primary-foreground sm:px-12 lg:px-16 lg:py-20'>
                <TreeRings className='pointer-events-none absolute -top-32 -right-32 size-[34rem] text-primary-foreground/10' />
                <TreeRings className='pointer-events-none absolute -bottom-56 -left-40 size-[28rem] text-primary-foreground/[0.07]' />

                <div className='relative grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-8'>
                        <Eyebrow tone='light'>
                            Start your project
                        </Eyebrow>

                        <h2 id='get-started-title' className='mt-4 font-heading text-4xl leading-[1.05] font-medium tracking-tight text-balance sm:text-5xl'>
                            {title}
                        </h2>

                        <p className='mt-5 max-w-2xl text-lg leading-relaxed text-primary-foreground/80'>
                            {description}
                        </p>
                    </div>

                    <div className='flex flex-wrap gap-3 lg:col-span-4 lg:justify-end'>
                        <Link
                            href={primaryHref}
                            className={buttonVariants({ variant: 'light', size: 'lg' })}
                        >
                            {primaryLabel}
                            <ArrowRight className='size-4' />
                        </Link>

                        <Link
                            href={secondaryHref}
                            className={cn(buttonVariants({ variant: 'outline-light', size: 'lg' }))}
                        >
                            {secondaryLabel}
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    )
}
